import { AlertOctagon, CheckCircle2, Terminal } from "lucide-react";
import { Badge } from "@/components/ui/badge";

function CommandBlock({ children }: { children: string }) {
  return (
    <pre className="mt-2 overflow-x-auto whitespace-pre-wrap rounded-md bg-slate-950 p-3 font-mono text-[11px] leading-relaxed text-slate-100 selection:bg-blue-500/50">
      {children}
    </pre>
  );
}

export function OperationalResetGuide() {
  return (
    <details open className="rounded-xl border border-red-200 bg-card shadow-sm">
      <summary className="cursor-pointer list-none px-4 py-3 marker:hidden">
        <div className="flex flex-wrap items-center gap-2">
          <AlertOctagon className="h-4 w-4 text-red-600" />
          <h2 className="text-sm font-semibold">一次性全量运营数据清零</h2>
          <Badge variant="warning">仅 CLI</Badge>
          <span className="text-[11px] text-muted-foreground">切换分支时使用</span>
        </div>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          清空分发、用量、Webhook 投递、账单、审计和全部 Key；保留协议、模板、站点、账号、供应商、分销商和 Webhook 配置。
        </p>
      </summary>

      <div className="space-y-4 border-t px-4 py-4 text-xs">
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5 leading-relaxed text-red-900">
          <strong>高风险操作：</strong>执行后全部 Key 与 <code>audit_log</code> 都会清空，数据库内不保留本次操作记录。
          唯一证据是全库备份、<code>manifest.json</code> 和 <code>reset-report.json</code>。
          本页面只提供指引，不会替你执行命令。
        </div>

        <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2.5 leading-relaxed text-blue-900">
          <strong>先复制本节命令和检查项。</strong>执行前必须停止后端写入，并由第二人复核 dry-run。
          将 <code>&lt;profile&gt;</code>、<code>&lt;sys-user&gt;</code>、<code>&lt;timestamp&gt;</code> 和
          <code>&lt;restore-db&gt;</code> 替换为真实值。生产环境保留 <code>--allow-production</code>，非生产环境删除它。
        </div>

        <ol className="space-y-4">
          <li>
            <div className="flex items-center gap-1.5 font-semibold"><Terminal className="h-3.5 w-3.5" />1. 检查命令存在</div>
            <CommandBlock>{`bash scripts/biz.sh reset-operational-data --help`}</CommandBlock>
            <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
              如果命令不可用，立即停止并联系开发。禁止根据文档手工拼接删除 SQL。
            </p>
          </li>

          <li>
            <div className="font-semibold">2. 确认目标并停止写入</div>
            <CommandBlock>{`profile=<profile> bash scripts/biz.sh status
profile=<profile> bash scripts/biz.sh stop
profile=<profile> bash scripts/biz.sh status`}</CommandBlock>
            <p className="mt-1.5 text-[11px] text-muted-foreground">核对 host、database 和 profile；确认后端不是 RUNNING。</p>
          </li>

          <li>
            <div className="font-semibold">3. 创建并校验全库备份</div>
            <CommandBlock>{`export BIZCONSOLE_PROFILE=<profile>
bash scripts/backup.sh
pg_restore -l backups/<timestamp>/db.dump >/dev/null && echo BACKUP_OK`}</CommandBlock>
            <p className="mt-1.5 text-[11px] text-muted-foreground">记录备份路径和 SHA-256；备份为空或校验失败时停止。</p>
          </li>

          <li>
            <div className="font-semibold">4. 构建并执行当前分支迁移</div>
            <CommandBlock>{`profile=<profile> bash scripts/biz.sh build backend
profile=<profile> bash scripts/biz.sh migrate`}</CommandBlock>
            <p className="mt-1.5 text-[11px] text-muted-foreground">迁移失败时停止。只有确认旧配置已迁移或可丢弃时才继续。</p>
          </li>

          <li>
            <div className="font-semibold">5. 执行 dry-run</div>
            <CommandBlock>{`profile=<profile> bash scripts/biz.sh reset-operational-data \\
  --dry-run \\
  --sys-user <sys-user> \\
  --allow-production`}</CommandBlock>
            <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
              核对目标库、清空表、每表行数、总行数和归档空间。出现未知表、外键冲突或异常行数时停止。
            </p>
          </li>

          <li>
            <div className="font-semibold">6. 第二人复核后执行</div>
            <CommandBlock>{`profile=<profile> bash scripts/biz.sh reset-operational-data \\
  --execute \\
  --confirm RESET_OPERATIONAL_DATA \\
  --sys-user <sys-user> \\
  --backup backups/<timestamp>/db.dump \\
  --allow-production`}</CommandBlock>
            <p className="mt-1.5 text-[11px] text-muted-foreground">不要跳过 dry-run、备份、sys 身份或生产保护。</p>
          </li>

          <li>
            <div className="font-semibold">7. 核对归档与执行结果</div>
            <CommandBlock>{`ARCHIVE_DIR=backups/archive/<timestamp>_operational_reset
cat "$ARCHIVE_DIR/manifest.json"
cat "$ARCHIVE_DIR/reset-report.json"`}</CommandBlock>
            <div className="mt-2 grid gap-1.5 text-[11px] sm:grid-cols-2">
              {[
                "reset-report 状态为 committed",
                "所有清空表行数为 0",
                "Key 页面列表为空",
                "audit_rows_after 为 0",
                "Key 以外配置行数不变",
                "备份 SHA-256 与报告一致",
                "归档文件 SHA-256 与 manifest 一致",
              ].map((item) => (
                <div key={item} className="flex items-start gap-1.5"><CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-green-600" />{item}</div>
              ))}
            </div>
          </li>

          <li>
            <div className="font-semibold">8. 启动并冒烟</div>
            <CommandBlock>{`profile=<profile> bash scripts/biz.sh start`}</CommandBlock>
            <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
              确认 sys 可登录，配置可读，历史页面为空，并完成一条测试分发或用量刷新。
            </p>
          </li>
        </ol>

        <details className="rounded-md border bg-muted/20 px-3 py-2.5">
          <summary className="cursor-pointer font-semibold">失败处理与回滚</summary>
          <ul className="mt-2 list-disc space-y-1.5 pl-5 text-[11px] leading-relaxed text-muted-foreground">
            <li>命令缺失、未知表、外键冲突或备份失败：停止，不要执行。</li>
            <li>锁超时：确认后端已停止，再重新 dry-run。</li>
            <li>报告停在 archived：数据可能已提交，先核对数据库，禁止直接重跑。</li>
            <li>配置缺失或结果异常：停止服务，使用全库备份恢复到新库。</li>
          </ul>
          <CommandBlock>{`BIZCONSOLE_PROFILE=<profile> bash scripts/restore.sh \\
  --from backups/<timestamp>/db.dump \\
  --to <restore-db>`}</CommandBlock>
          <p className="mt-1.5 text-[11px] text-muted-foreground">逐表 csv.gz 仅用于查证，不能代替完整恢复。</p>
        </details>

        <p className="text-[11px] text-muted-foreground">
          详细依据：doc/v3/091-分支切换运营数据清零_开发文档_v1_20261026.md 与 doc/v3/092-分支切换运营数据清零_操作手册_v1_20261026.md。
        </p>
      </div>
    </details>
  );
}
